import {
  ComponentRef,
  Injectable,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { ChartCaptureService } from './chart-capture.service';
import { ProjectService } from './project.service';
import { ChartViewportService } from './chart-viewport.service';
import { TelemetryStoreService } from './telemetry-store.service';
import { TelemetryFamily } from './telemetry-cursor';
import { setReportMode } from './chart-theme';
import { MsalService } from '@azure/msal-angular';
import { VERICURE_API_SCOPE, requireEntraAuth } from '../auth/api-scope';
import { resolveActiveAccount } from '../auth/active-account';
import { CuringComponent } from '../curing/curing.component';
import { GraphComponent } from '../graph/graph.component';
import { HealthComponent } from '../health/health.component';
import { TemperatureComponent } from '../temperature/temperature.component';

/**
 * Offscreen render-on-export.
 *
 * Report chart images are client-captured from SciChart surfaces, which are only
 * registered by their route components' ngOnInit — a tab that was never visited
 * has no surface and its chart used to be silently missing from the PDF. On
 * export, this service instantiates the owning components for any unregistered
 * capture names into an offscreen host (real size, left:-99999px — NOT
 * display:none, which would freeze SciChart layout at 0×0), waits for the
 * surfaces to register and render (data replays from the ProjectService
 * BehaviorSubjects), lets the capture run, then destroys the components.
 *
 * The host ViewContainerRef is provided by MainLayoutComponent (the offscreen
 * div lives in its template).
 */
/**
 * A single history fetch. Backfills measured 20-28 s on real projects.
 *
 * THIS CONSTANT EXISTED AND WAS NEVER APPLIED — declared here, referenced
 * nowhere. It is now the deadline on each ensureHistorical call; see
 * fetchHistoryFor. Declared-but-unused is how the export came to have no bound
 * at all on its longest phase.
 */
const FETCH_TIMEOUT_MS = 60000;

/**
 * Warming the API token. MSAL's acquireTokenSilent can sit indefinitely — a
 * hidden-iframe renewal against an unreachable authority neither resolves nor
 * rejects, so the try/catch around it catches nothing.
 */
const TOKEN_WARM_TIMEOUT_MS = 20000;

/**
 * How long to wait for fetched data to reach the surfaces.
 *
 * Must exceed the fetch itself: the previous 20 s was shorter than a 20-28 s
 * backfill, so even when a fetch was in flight the wait expired first and the
 * capture ran against empty surfaces.
 */
const DATA_WAIT_MS = 90000;

@Injectable({ providedIn: 'root' })
export class ExportPrerenderService {
  /** Capture name → route component that registers it. */
  private static readonly OWNERS: Array<{
    names: string[];
    component: Type<unknown>;
  }> = [
    { names: ['curingGraph'], component: CuringComponent },
    { names: ['zones', 'temperatureOverDistance'], component: TemperatureComponent },
    { names: ['temperatureOverTime', 'heatmap3D'], component: GraphComponent },
    { names: ['healthLossGraph'], component: HealthComponent },
  ];

  private hostVcr?: ViewContainerRef;

  constructor(
    private chartCapture: ChartCaptureService,
    private projectService: ProjectService,
    private chartViewport: ChartViewportService,
    private msalService: MsalService,
    private telemetryStore: TelemetryStoreService
  ) {}

  /**
   * Get the API token into MSAL's cache BEFORE the history calls go out.
   *
   * The export fires three fetches at once. Each one's interceptor asks for a
   * token, and when the cached grant does not already cover the scope that means
   * three concurrent hidden-iframe round trips — which is how the losers ended up
   * with no bearer at all, sending unauthenticated history requests that an
   * Entra-tied project answers with 403. Warming once, first, means the
   * interceptor's own calls are cache hits.
   *
   * Failure is logged, not thrown: a Google or email session has no MSAL account
   * and legitimately has no token to warm, and the function-key path still
   * applies to projects that permit it.
   */
  private async warmApiToken(): Promise<void> {
    if (!requireEntraAuth()) {
      return;
    }
    try {
      const instance = this.msalService?.instance;
      const account =
        resolveActiveAccount(instance);
      if (!instance || !account) {
        return;
      }
      await this.withDeadline(
        instance.acquireTokenSilent({
          scopes: [VERICURE_API_SCOPE],
          account: account as never,
        }),
        TOKEN_WARM_TIMEOUT_MS,
        'token warm'
      );
      console.log('ExportPrerender: API token warmed');
    } catch (err) {
      console.warn(
        'ExportPrerender: could not warm the API token — history calls may go ' +
          'out unauthenticated and be refused by Entra-tied projects',
        (err as { errorCode?: string })?.errorCode ?? err
      );
    }
  }

  /** Which telemetry family each capture name needs on the wire. */
  private static readonly FAMILIES: Record<string, Array<'temperature' | 'zone' | 'loss'>> = {
    curingGraph: ['temperature', 'loss'],
    // Zones are DERIVED from raw temperatures on the liner window, which comes
    // out of the temperature backfill. So zones needs temperature first.
    zones: ['temperature', 'zone'],
    temperatureOverDistance: ['temperature'],
    temperatureOverTime: ['temperature'],
    heatmap3D: ['temperature'],
    healthLossGraph: ['loss'],
  };

  /**
   * Fetch the history these surfaces need.
   *
   * THE BUG THIS FIXES: the previous version assumed the data would replay from
   * the ProjectService BehaviorSubjects. It does not. loadHistoricalTelemetry —
   * the only thing a project change runs — fetches TEMPERATURE ONLY. Zone and
   * loss are fetched by the tab components themselves, in their own ngOnInit. So
   * for a tab the user never opened, the fetch never happened and the subject is
   * empty: there is nothing to replay, which is precisely why clicking through
   * the tabs first was a working workaround.
   *
   * Called directly rather than through requestHistoryReload, which is throttled
   * to one call per 15 s — a throttle the curing baseline request had usually
   * just consumed, so the export's own request was silently dropped.
   */
  private async fetchHistoryFor(names: string[]): Promise<void> {
    const project = this.projectService.selectedProjectName;
    if (!project) {
      return;
    }

    const needed = new Set<TelemetryFamily>();
    for (const n of names) {
      for (const f of ExportPrerenderService.FAMILIES[n] ?? []) {
        needed.add(f);
      }
    }
    if (needed.size === 0) {
      return;
    }
    // Token first, then the fetches — see warmApiToken.
    await this.warmApiToken();

    console.log(
      `ExportPrerender: fetching history for [${[...needed].join(', ')}]`
    );

    // ONE RETRIEVAL PATH. This used to hand-roll the temperature fetch and do
    // LESS than the live loader: setTempData and loadMarkers, but no
    // advanceCursor and no notes. A report generated during a live cure then
    // left report-resolution rows behind a cursor still pointing at the live
    // position, and the next tick appended live rows onto report rows in one
    // series. Going through ensureHistorical means there is no second copy to
    // drift, which is the only durable fix for that class of bug.
    const fetch = (family: TelemetryFamily) =>
      this.withDeadline(
        this.projectService
          .ensureHistorical(project, family, this.chartViewport.paramsFor(family, null))
          .catch((err) => this.logFetchFailure(family, err)),
        FETCH_TIMEOUT_MS,
        `history fetch for ${family}`
      );

    // PHASE 1: temperature, because the zone fold's liner window comes out of it
    // (the same ordering HistoricalLoadService uses). PHASE 2: the rest, in parallel.
    if (needed.has('temperature')) {
      await fetch('temperature');
    }
    await Promise.all([...needed].filter((f) => f !== 'temperature').map(fetch));
  }

  registerHost(vcr: ViewContainerRef): void {
    this.hostVcr = vcr;
  }

  clearHost(): void {
    this.hostVcr = undefined;
  }

  /**
   * Instantiates the components owning any of the requested capture names that
   * are not currently registered, and resolves once their surfaces have
   * registered (or after a bounded wait — the capture then falls back to the
   * server-side placeholder for anything still missing). Returns the created
   * ComponentRefs; the caller MUST pass them to destroy() when done.
   */
  async prerenderMissing(names: string[]): Promise<ComponentRef<unknown>[]> {
    if (!this.hostVcr) {
      console.warn('ExportPrerender: no offscreen host registered');
      return [];
    }

    // THE REPORT WRITES TO ITS OWN VARIANT, it is no longer merely blocked from
    // writing.
    //
    // Everything below drives the ProjectService subjects with the REPORT's own
    // windowed, report-resolution payloads. Those reach the same store the live
    // charts read, so this used to be handled by SUSPENDING ingestion for the
    // duration — correct, but it meant a report could not keep any of the data it
    // fetched, and every report re-fetched from scratch.
    //
    // Under a variant those rows land in 'report' slots instead: the live buffers
    // are untouched because nothing writes to them, rather than because writing
    // is switched off. Live ticks arriving mid-report still reach 'live', which
    // suspension could not allow.
    //
    // withVariant drops the report slots in its finally, so a fetch timeout or a
    // blank capture cannot leak a second full window of every family.
    return this.telemetryStore.withVariant('report', () =>
      this.prerenderMissingInner(names)
    );
  }

  private async prerenderMissingInner(
    names: string[]
  ): Promise<ComponentRef<unknown>[]> {
    // Re-asserted rather than relied on from the caller: narrowing does not
    // cross a method boundary, and a host that vanished between the two is a
    // real (if unlikely) teardown race.
    const hostVcr = this.hostVcr;
    if (!hostVcr) {
      return [];
    }

    // EVERY report chart is rendered offscreen, not just the ones no tab has
    // built. Report colours are resolved at CONSTRUCTION, so a chart built
    // earlier by a live tab carries the console's palette — including the white
    // current-temperature line that is invisible on a white page and reads as a
    // missing series. Rebuilding them all is what makes the report deterministic
    // regardless of which tabs the user happened to visit.
    //
    // Safe because unregister() now matches on surface identity: destroying
    // these offscreen components cannot clear a live tab's registration.
    const missing = names.slice();
    if (missing.length === 0) {
      return [];
    }

    const owners = ExportPrerenderService.OWNERS.filter((o) =>
      o.names.some((n) => missing.includes(n))
    );

    // REPORT MODE ON BEFORE ANY COMPONENT IS BUILT.
    //
    // The components resolve their colours at construction, so this must be set
    // before createComponent and cleared only after the capture. Charts built
    // while it is on come out print-coloured in every respect — series, legend
    // swatches and band fills alike — because they are all constructed from the
    // same values rather than patched afterwards.
    setReportMode(true);

    const refs: ComponentRef<unknown>[] = [];
    for (const owner of owners) {
      console.log(
        `ExportPrerender: rendering ${owner.component.name} offscreen for [${owner.names.join(', ')}]`
      );
      const ref = hostVcr.createComponent(owner.component);
      const el = ref.location.nativeElement as HTMLElement;
      // Give the component a real layout box so SciChart can create and size
      // its surfaces (the inner chart divs size via flex against this box).
      el.style.display = 'flex';
      el.style.width = '1400px';
      el.style.height = '800px';
      ref.changeDetectorRef.detectChanges();
      refs.push(ref);
    }

    // Surfaces are created asynchronously (SciChartSurface.create in ngOnInit) —
    // poll for registration, bounded.
    await this.waitFor(
      () => missing.every((n) => this.chartCapture.has(n)),
      15000,
      150
    );

    const neverRegistered = missing.filter((n) => !this.chartCapture.has(n));
    if (neverRegistered.length > 0) {
      console.warn(
        `ExportPrerender: surfaces never registered for [${neverRegistered.join(', ')}]`
      );
    }

    // Fetch what these surfaces need. Creating the component is not enough: for
    // a tab never opened, nobody has ever asked the server for its family.
    await this.fetchHistoryFor(missing);

    // NOW WAIT FOR DATA, which is the step this service was missing.
    //
    // It previously waited only for REGISTRATION and then a flat 600 ms.
    // Registration happens the instant SciChartSurface.create() returns, long
    // before the component has subscribed to ProjectService and bound a series.
    // An empty surface still renders — successfully, immediately, and blank — so
    // the capture that followed produced a valid PNG of an empty chart. That is
    // why a chart came out blank unless its tab had been visited: visiting it
    // was the only thing that ever got data onto the surface in time.
    //
    // The data itself replays from the ProjectService BehaviorSubjects, so it
    // arrives without a refetch; what varies is how long the component takes to
    // subscribe, fold and draw. Poll for it rather than guessing an interval.
    await this.waitFor(
      () => missing.every((n) => this.chartCapture.hasRenderedData(n)),
      DATA_WAIT_MS,
      150
    );

    const stillEmpty = missing.filter(
      (n) => !this.chartCapture.hasRenderedData(n)
    );
    if (stillEmpty.length > 0) {
      console.warn(
        `ExportPrerender: NO DATA on surfaces [${stillEmpty.join(', ')}] — ` +
          `their pages will fall back to a cached snapshot or the server placeholder`
      );
    }

    // One more frame so the first data render reaches the canvas before the
    // high-res pass resizes and re-renders it.
    await new Promise((r) => setTimeout(r, 300));

    return refs;
  }

  /**
   * Log what actually went wrong, not "[object]".
   *
   * The previous catch printed the error object itself, which stringifies to
   * "[object HttpErrorResponse]" and hides the one field that identifies the
   * problem. A failure here costs a whole page of the report, so it is worth
   * saying which request failed and how.
   */
  private logFetchFailure(family: string, err: unknown): void {
    const e = err as {
      name?: string;
      status?: number;
      statusText?: string;
      url?: string;
      message?: string;
      error?: unknown;
    };
    console.error(
      `ExportPrerender: ${family} fetch FAILED — ` +
        `name=${e?.name ?? '?'} status=${e?.status ?? '?'} ` +
        `statusText=${e?.statusText ?? '?'} message=${e?.message ?? '?'}`,
      { url: e?.url, body: e?.error, raw: err }
    );
  }

  destroy(refs: ComponentRef<unknown>[]): void {
    // Off again the moment the offscreen charts are gone. A tab built after this
    // point must be the console's own colours, so leaving it on would make the
    // next chart the user opens print-themed.
    setReportMode(false);
    refs.forEach((ref) => {
      try {
        ref.destroy();
      } catch (err) {
        console.warn('ExportPrerender: component destroy failed', err);
      }
    });
  }

  /**
   * Settle when `p` settles, or after `ms`, whichever comes first.
   *
   * THE FAILURE THIS EXISTS FOR IS NOT A REJECTION, IT IS A SILENCE. A promise
   * that never settles is not caught by try/catch and not reported by .catch()
   * — the await simply never returns. Both awaits in this service's fetch phase
   * were that shape, so a stalled token renewal or a history call that never
   * came back left `isGenerating` true forever: no error, no file, no POST, and
   * nothing for the operator to read but a spinner. MEASURED on beta-dev: still
   * running at 321 s with every surface healthy and lastMissing empty.
   *
   * Resolves rather than rejects, because a missed token warm or a missed
   * backfill is not fatal to the export. The capture that follows will find
   * empty surfaces, report them missing BY NAME, and the report will refuse for
   * a reason the operator can act on. A refusal beats a hang: the refusal says
   * what happened.
   */
  private withDeadline<T>(
    p: Promise<T>,
    ms: number,
    label: string
  ): Promise<T | null> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (v: T | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(v);
      };
      const timer = setTimeout(() => {
        console.warn(
          `ExportPrerender: ${label} exceeded ${ms} ms — continuing without it`
        );
        finish(null);
      }, ms);
      p.then(
        (v) => finish(v),
        (err) => {
          console.warn(`ExportPrerender: ${label} failed`, err);
          finish(null);
        }
      );
    });
  }

  private waitFor(
    condition: () => boolean,
    timeoutMs: number,
    pollMs: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        if (condition() || Date.now() - started > timeoutMs) {
          resolve();
        } else {
          setTimeout(check, pollMs);
        }
      };
      check();
    });
  }
}
